//this is the original Questionextraction.jsx file which is used 
import API from "../api";
import TextInputBox from "../components/TextInputBox";
import FileUploader from "../components/FileUploader";
import Loader from "../components/Loader";
import "../styles/questionExtraction.css";
import { useToast } from "../context/ToastContext";
import { mainAPI } from "../api";
import { useState, useRef } from "react";
// ✅ Modal Component
function TestDetailsModal({ onSubmit, onCancel }) {
  const [title, setTitle] = useState("Extracted Test");
  const [description, setDescription] = useState("Generated from extracted questions");
  const [type, setType] = useState("practice");
  const [visibility, setVisibility] = useState("public");
  const { showToast } = useToast();
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-96 p-6">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
          📝 Save Extracted Test
        </h2>

        <label className="block mb-2 text-gray-700 font-medium">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full border rounded-md p-2 mb-4 focus:ring focus:ring-blue-200"
        />

        <label className="block mb-2 text-gray-700 font-medium">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows="3"
          className="w-full border rounded-md p-2 mb-4 focus:ring focus:ring-blue-200"
        ></textarea>

        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <label className="block mb-1 text-gray-700 font-medium">Test Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full border rounded-md p-2"
            >
              <option value="practice">Practice</option>
              <option value="timed">Timed</option>
              <option value="exam">Exam</option>
              <option value="adaptive">Adaptive</option>
            </select>
          </div>

          <div className="flex-1">
            <label className="block mb-1 text-gray-700 font-medium">Visibility</label>
            <select
              value={visibility}
              onChange={(e) => setVisibility(e.target.value)}
              className="w-full border rounded-md p-2"
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
            </select>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <button
            onClick={onCancel}
            className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(title, description, type, visibility)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function QuestionExtraction() {
  const [message, setMessage] = useState({ text: "", type: "" }); 
  const [text, setText] = useState("");
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { showToast } = useToast();
  const resultRef = useRef(null);
  
  // ✅ Extract questions
  const handleExtract = async () => {
    if (!text.trim()) return showToast("Please enter or upload text first!");

    setLoading(true);
    try {
      const { data } = await API.post("/extract-questions", { text });
      setQuestions(data.data.questions);
      setSubmitted(false);
      // 👇 Scroll after small delay
    setTimeout(() => {
      resultRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }, 300);
    } catch (error) {
      console.error(error);
      setMessage({ text: "❌ Error extracting questions!", type: "error" });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delete specific question
  const handleDelete = (index) => {
    const updated = questions.filter((_, i) => i !== index);
    setQuestions(updated);
  };

  // ✅ Submit (called from modal)
  const handleSubmit = async (title, description, type, visibility) => {
    if (!questions.length) return showToast("No questions to submit!");

    try {
      const letters = ["A","B","C","D"];

const formattedQuestions = questions.map((q) => {

  const hasOptions = Array.isArray(q.options) && q.options.length >= 2;

  // TRUE / FALSE DETECTION
  if (
    hasOptions &&
    q.options.length === 2 &&
    q.options.some(o => o.toLowerCase() === "true") &&
    q.options.some(o => o.toLowerCase() === "false")
  ) {
    return {
      question: q.question?.trim(),
      type: "truefalse",
      options: [
        { key: "A", text: "True", media: null },
        { key: "B", text: "False", media: null }
      ],
      answer: q.answer?.toLowerCase().trim(),
      media: null
    };
  }

  // MCQ
  if (hasOptions) {

    const options = q.options.map((opt, i) => ({
      key: letters[i],
      text: typeof opt === "string" ? opt.trim() : opt.text?.trim(),
      media: null
    }));

   

    return {
      question: q.question?.trim(),
      type: "mcq",
      options,
      answer: q.answer?.trim(),
      media: null
    };
  }

  // TEXT TYPE
  return {
    question: q.question?.trim(),
    type: "text",
    options: [],
    answer: q.answer?.trim(),
    media: null
  };
});
await API.post("/save-extracted", {
  questions: formattedQuestions,
  title,
  description,
  isPublic: visibility === "public"
});

      setMessage({ text: "✅ Test created successfully!", type: "success" });
      setSubmitted(true);
      setShowModal(false);
    } catch (error) {
      console.error(error);
      setMessage({ text: "❌ Error saving test!", type: "error" });

    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5efe6] via-[#f9f6f1] to-[#efe7dc] flex flex-col items-center py-10">
      <h2 className="text-3xl font-bold text-gray-800 mb-3 flex items-center gap-2">
        🧠 Question Extraction Tool
      </h2>
      <p className="text-gray-500 mb-8 text-center w-3/4 md:w-1/2">
        Paste text or upload a file — the system will extract questions automatically.
      </p>
{/* ================= TWO COLUMN LAYOUT ================= */}
<div className="w-11/12 lg:w-10/12 grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">

  {/* 🤖 ChatGPT Prompt Helper */}
  <div className="bg-white shadow-lg rounded-2xl p-6 border border-gray-100">
    <h3 className="text-xl font-semibold text-gray-800 mb-2">
      🤖 Generate Questions using ChatGPT
    </h3>

    <p className="text-sm text-gray-500 mb-4">
      Use this prompt in ChatGPT to generate questions in an extractable format.
    </p>

    <pre className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-xs whitespace-pre-wrap mb-4 max-h-72 overflow-auto">
{`Convert the following content into questions strictly in this format:

Question: <question text>
A) <option 1>
B) <option 2>
C) <option 3>
D) <option 4>
Answer: <correct option text or option letter>

Rules:
- Always write "Question:" at the start
- Options must start with A), B), C), D)
- Answer must start with "Answer:"
- For True/False questions, write:
  Answer: True OR Answer: False
- Do not add explanations
- Do not use markdown
- Do not skip Answer line

Content:
<PASTE YOUR CONTENT HERE>
`}
    </pre>

    <div className="flex flex-wrap gap-3">
      <a
        href="https://chat.openai.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium"
      >
        🚀 Open ChatGPT
      </a>

      <button
        onClick={() => {
          navigator.clipboard.writeText(`Convert the following content into questions strictly in this format:

Question: <question text>
A) <option 1>
B) <option 2>
C) <option 3>
D) <option 4>
Answer: <correct option text or option letter>

Rules:
- Always start with "Question:"
- Options must be A), B), C), D)
- Answer must start with "Answer:"
- No explanations
- No markdown`);
          showToast("✅ Prompt copied!");
        }}
        className="bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-md font-medium"
      >
        📋 Copy Prompt
      </button>
    </div>
  </div>

  {/* ✍️ Input Box Section */}
  <div className="bg-white shadow-lg rounded-2xl p-6 border border-gray-100">
    <TextInputBox text={text} setText={setText} />

    <div className="mt-4">
      <FileUploader setText={setText} />
    </div>

    <button
      onClick={handleExtract}
      className="w-full mt-6 px-5 py-3 rounded-xl font-semibold
                 bg-gradient-to-r from-[#7f5539] to-[#9c6644]
                 text-white shadow hover:opacity-90 transition"
    >
      ⚙️ Extract Questions
    </button>
  </div>

</div>

      {loading && <Loader />}
{message.text && (
  <div
    className={`fixed top-5 right-5 px-5 py-3 rounded-xl shadow-lg text-white font-medium transition-all duration-500 ${
      message.type === "success" ? "bg-green-600" : "bg-red-600"
    }`}
  >
    {message.text}
  </div>
)}

      {!loading && questions.length > 0 && (
        <div ref={resultRef} className="mt-10 w-11/12 md:w-2/3 lg:w-1/2 bg-white shadow-lg rounded-2xl p-6 border border-gray-100">
          <h3 className="text-2xl font-semibold mb-4 text-gray-700">
            🧠 Extracted Questions ({questions.length})
          </h3>

          {questions.map((q, i) => (
            <div
              key={i}
              className="flex justify-between items-start bg-gray-50 p-3 rounded-lg mb-3"
            >
              <div className="w-full">
                <p className="font-medium text-gray-800">
                  Q{i + 1}: {q.question}
                </p>

                <p className="text-sm text-blue-600 italic mt-1">
                  Type: {q.type ? q.type.toUpperCase() : "TEXT"}
                </p>

                {q.options && q.options.length > 0 && (
                  <ul className="list-disc ml-6 text-gray-700 mt-2">
                    {q.options.map((opt, idx) => (
                      <li key={idx}>{opt}</li>
                    ))}
                  </ul>
                )}

                <p className="text-gray-600 mt-2">
                  <span className="font-semibold">Answer:</span> {q.answer || "—"}
                </p>
              </div>

              <button
                onClick={() => handleDelete(i)}
                className="ml-4 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-md"
              >
                🗑️ Delete
              </button>
            </div>
          ))}

          {!submitted && (
            <button
              onClick={() => setShowModal(true)}
              className="mt-6 w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-xl transition-all duration-300"
            >
              ✅ Submit Selected Questions
            </button>
          )}
        </div>
      )}

      {/* ✅ Modal for test details */}
      {showModal && (
        <TestDetailsModal
          onSubmit={handleSubmit}
          onCancel={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
